import { copy } from '@ohear/shared'
import { Badge, Button, Card, ProgressBar } from '@ohear/ui'
/**
 * Actor project overview — landing page when an actor opens a project.
 *
 * Shows: character + episode summary, batch list with progress + status,
 * and quick links into Script Review or Record. Provides the "where am I"
 * context before the actor commits to a recording session.
 */
import { Link, createFileRoute } from '@tanstack/react-router'
import { useActorProject } from '../../../lib/api/projects'
import { useRequireRole } from '../../../lib/auth'
import { useT } from '../../../lib/i18n/use-t'

export const Route = createFileRoute('/actor/projects/$projectId/')({
  component: ActorProjectDetail,
})

function ActorProjectDetail() {
  useRequireRole(['actor'])
  const t = useT()
  const { projectId } = Route.useParams()
  const project = useActorProject(projectId)

  if (project.isLoading) return <p className="text-muted p-6">Loading project…</p>
  if (project.isError || !project.data) {
    return (
      <div className="p-6">
        <p className="text-amber-400">{t(copy.common.NOT_FOUND.load_project)}</p>
        <Link to="/actor" className="text-blue-400 hover:underline">
          Back to projects
        </Link>
      </div>
    )
  }
  const data = project.data
  const totalApproved = data.approved_cues
  const totalCues = data.total_cues
  const overallPct = totalCues > 0 ? (totalApproved / totalCues) * 100 : 0

  return (
    <section className="px-6 py-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{data.title}</h1>
          <p className="text-muted mt-1 text-sm">
            {data.client_name} ·{' '}
            {data.characters.map((c, i) => (
              <span key={c.character_id}>
                {i > 0 ? ' / ' : null}
                {c.display_name} <span className="font-mono">({c.character_code})</span>
              </span>
            ))}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/actor/projects/$projectId/script-review" params={{ projectId }}>
            <Button variant="ghost">📖 Script review</Button>
          </Link>
          <Link to="/actor/projects/$projectId/upload" params={{ projectId }}>
            <Button variant="ghost">⬆️ {t(copy.upload.BATCH.TITLE)}</Button>
          </Link>
          <Link to="/actor/projects/$projectId/record" params={{ projectId }}>
            <Button>🎤 Continue recording</Button>
          </Link>
        </div>
      </div>

      <Card className="mb-6 p-4">
        <div className="text-muted mb-2 flex justify-between text-xs">
          <span>Overall progress</span>
          <span>
            <strong className="text-[var(--text)]">{totalApproved}</strong> / {totalCues} cues
            approved
          </span>
        </div>
        <ProgressBar value={overallPct} />
      </Card>

      <h2 className="mb-2 text-sm uppercase tracking-wide text-muted">Batches</h2>
      <ul className="space-y-2">
        {data.batches.map((b) => {
          const pct = b.total_cues > 0 ? (b.approved_cues / b.total_cues) * 100 : 0
          return (
            <li key={b.batch_id}>
              <Card className="flex items-center gap-4 p-4">
                <div className="min-w-[6rem]">
                  <div className="text-faded text-xs">Batch</div>
                  <div className="text-lg font-semibold">{b.batch_number}</div>
                </div>
                <div className="flex-1">
                  <div className="text-muted mb-1 text-xs">
                    {b.approved_cues} / {b.total_cues} approved
                    {b.pickup_pending_cues > 0 && (
                      <span className="text-amber-400 ml-2">
                        · {b.pickup_pending_cues} pickup{b.pickup_pending_cues === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <ProgressBar value={pct} />
                </div>
                <div>{statusBadge(b.status)}</div>
                <div>
                  {b.status === 'approved' ? (
                    <Button variant="ghost" disabled>
                      Done
                    </Button>
                  ) : (
                    <Link to="/actor/projects/$projectId/record" params={{ projectId }}>
                      <Button>{b.status === 'not_started' ? 'Start' : 'Continue'}</Button>
                    </Link>
                  )}
                </div>
              </Card>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function statusBadge(status: 'not_started' | 'in_progress' | 'awaiting_review' | 'approved') {
  switch (status) {
    case 'not_started':
      return <Badge variant="gray">Not started</Badge>
    case 'in_progress':
      return <Badge variant="blue">In progress</Badge>
    case 'awaiting_review':
      return <Badge variant="amber">Awaiting review</Badge>
    case 'approved':
      return <Badge variant="green">Approved</Badge>
  }
}
